import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ArrowUpDown, Download, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { IMPACT_LABELS, TopShopStatusBadge } from "@/components/topshop/topShopShared";
import { AdminAppRow, ListRow, NOT_ASSIGNED, PARISHES, useTopShopApplications } from "./useTopShopAdmin";
import { TopShopApplicationSheet } from "./TopShopApplicationSheet";
import { MoveCohortButton } from "./MoveCohortButton";

type SortKey = "name" | "score" | "applied";

function csvCell(v: unknown) {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function FilterSelect({ value, onChange, label, options }: {
  value: string; onChange: (v: string) => void; label: string; options: { value: string; label: string }[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[160px]" aria-label={label}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{`All: ${label}`}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function TopShopApplicationsTab({ programId, lists }: {
  programId: string; lists?: { types: ListRow[]; communities: ListRow[]; focusAreas: ListRow[] };
}) {
  const { data: apps = [], isLoading, error } = useTopShopApplications(programId);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [parish, setParish] = useState("all");
  const [type, setType] = useState("all");
  const [community, setCommunity] = useState("all");
  const [impact, setImpact] = useState("all");
  const [focus, setFocus] = useState("all");
  const [hasPhotos, setHasPhotos] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("applied");
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const focusNameMap = useMemo(() => new Map((lists?.focusAreas ?? []).map((f) => [f.id, f.name])), [lists]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = apps.filter((a) => {
      if (q && ![a.full_name, a.email, a.business_name].some((v) => v?.toLowerCase().includes(q))) return false;
      if (status !== "all" && a.status !== status) return false;
      if (parish !== "all" && a.parish !== parish) return false;
      if (type !== "all" && a.business_type_id !== type) return false;
      if (community === NOT_ASSIGNED) { if (a.community_id) return false; }
      else if (community !== "all" && a.community_id !== community) return false;
      if (impact !== "all" && a.hurricane_impact_level !== impact) return false;
      if (focus !== "all" && !a.focus_area_ids.includes(focus)) return false;
      if (hasPhotos === "yes" && a.photo_count === 0) return false;
      if (hasPhotos === "no" && a.photo_count > 0) return false;
      return true;
    });
    const dir = sortAsc ? 1 : -1;
    return rows.sort((a, b) => {
      if (sortKey === "name") return dir * (a.full_name ?? "").localeCompare(b.full_name ?? "");
      if (sortKey === "score") return dir * (a.total_score - b.total_score);
      return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    });
  }, [apps, search, status, parish, type, community, impact, focus, hasPhotos, sortKey, sortAsc]);

  const counts = useMemo(() => ({
    total: apps.length,
    submitted: apps.filter((a) => a.status === "submitted").length,
    accepted: apps.filter((a) => a.status === "accepted").length,
    waitlisted: apps.filter((a) => a.status === "waitlisted").length,
    declined: apps.filter((a) => a.status === "declined").length,
    withdrawn: apps.filter((a) => a.status === "withdrawn").length,
    completed: apps.filter((a) => a.status === "completed").length,
    toMove: apps.filter((a) => (a.status === "completed" || a.status === "withdrawn") && !a.moved_to_community_at).length,
    photos: apps.filter((a) => a.photo_count > 0).length,
  }), [apps]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortAsc(!sortAsc);
    else { setSortKey(k); setSortAsc(k === "name"); }
  };

  const exportCsv = () => {
    const header = ["Name", "Email", "Phone", "WhatsApp number", "Business", "Type", "Parish", "Community", "Impact", "Photos", "Score", "Status", "Applied", "Updated by applicant", "Moved to community", "Focus areas", "Why join", "Hurricane description", "Admin notes"];
    const lines = filtered.map((a: AdminAppRow) => [
      a.full_name, a.email, a.phone, a.whatsapp_number, a.business_name, a.type_name, a.parish, a.community_name,
      IMPACT_LABELS[a.hurricane_impact_level] ?? a.hurricane_impact_level, a.photo_count, a.total_score, a.status,
      format(new Date(a.created_at), "yyyy-MM-dd"),
      a.applicant_updated_at ? format(new Date(a.applicant_updated_at), "yyyy-MM-dd") : "",
      a.moved_to_community_at ? format(new Date(a.moved_to_community_at), "yyyy-MM-dd") : "",
      a.focus_area_ids.map((id) => focusNameMap.get(id) ?? id).join("; "),
      a.why_join, a.hurricane_impact_description, a.admin_notes,
    ].map(csvCell).join(","));
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `top-shop-applications-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const SortHead = ({ k, children }: { k: SortKey; children: string }) => (
    <TableHead>
      <button type="button" className="inline-flex items-center gap-1 font-medium" onClick={() => toggleSort(k)}>
        {children}<ArrowUpDown className="h-3 w-3" />
      </button>
    </TableHead>
  );

  const tiles = [
    ["Total applications", counts.total], ["Submitted", counts.submitted], ["Accepted", counts.accepted],
    ["Waitlisted", counts.waitlisted], ["Declined", counts.declined], ["Withdrawn", counts.withdrawn], ["Completed", counts.completed], ["With photos", counts.photos],
  ] as const;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <MoveCohortButton programId={programId}
          acceptedCount={apps.filter((a) => a.status === "accepted").length}
          alreadyDoneCount={counts.toMove} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-3">
        {tiles.map(([label, n]) => (
          <Card key={label}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{n}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search name, email, business" aria-label="Search" className="w-[240px]" value={search} onChange={(e) => setSearch(e.target.value)} />
        <FilterSelect label="Status" value={status} onChange={setStatus} options={[
          { value: "submitted", label: "Submitted" }, { value: "accepted", label: "Accepted" },
          { value: "waitlisted", label: "Waitlisted" }, { value: "declined", label: "Declined" },
          { value: "withdrawn", label: "Withdrawn" }, { value: "completed", label: "Completed" }]} />
        <FilterSelect label="Parish" value={parish} onChange={setParish} options={PARISHES.map((p) => ({ value: p, label: p }))} />
        <FilterSelect label="Type" value={type} onChange={setType} options={(lists?.types ?? []).map((t) => ({ value: t.id, label: t.name }))} />
        <FilterSelect label="Community" value={community} onChange={setCommunity} options={[
          ...[...(lists?.communities ?? []).filter((t) => !t.is_catch_all), ...(lists?.communities ?? []).filter((t) => t.is_catch_all)].map((t) => ({ value: t.id, label: t.name })),
          { value: NOT_ASSIGNED, label: "Not assigned" },
        ]} />
        <FilterSelect label="Impact" value={impact} onChange={setImpact} options={Object.entries(IMPACT_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
        <FilterSelect label="Focus area" value={focus} onChange={setFocus} options={(lists?.focusAreas ?? []).map((t) => ({ value: t.id, label: t.name }))} />
        <FilterSelect label="Photos" value={hasPhotos} onChange={setHasPhotos} options={[{ value: "yes", label: "Has photos" }, { value: "no", label: "No photos" }]} />
        <Button variant="outline" onClick={exportCsv} disabled={!filtered.length} className="ml-auto">
          <Download className="h-4 w-4 mr-2" />Export CSV
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">Showing {filtered.length} of {apps.length}</p>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : error ? (
            <p className="p-6 text-sm text-destructive">Could not load applications.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHead k="name">Name</SortHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Parish</TableHead>
                  <TableHead>Community</TableHead>
                  <TableHead>Impact</TableHead>
                  <TableHead>Photos</TableHead>
                  <SortHead k="score">Score</SortHead>
                  <TableHead>Status</TableHead>
                  <SortHead k="applied">Applied</SortHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No applications match.</TableCell></TableRow>
                ) : filtered.map((a) => (
                  <TableRow key={a.id} className="cursor-pointer" onClick={() => setSelectedId(a.id)}>
                    <TableCell className="font-medium">{a.full_name ?? "-"}</TableCell>
                    <TableCell>{a.email ?? "-"}</TableCell>
                    <TableCell>{a.business_name}</TableCell>
                    <TableCell>{a.type_name ?? "-"}</TableCell>
                    <TableCell>{a.parish}</TableCell>
                    <TableCell>{a.community_name ?? "-"}</TableCell>
                    <TableCell>{IMPACT_LABELS[a.hurricane_impact_level] ?? a.hurricane_impact_level}</TableCell>
                    <TableCell>{a.photo_count}</TableCell>
                    <TableCell>{a.total_score}</TableCell>
                    <TableCell><TopShopStatusBadge status={a.status} /></TableCell>
                    <TableCell className="whitespace-nowrap">{format(new Date(a.created_at), "MMM d, yyyy")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TopShopApplicationSheet
        application={apps.find((a) => a.id === selectedId) ?? null}
        focusAreas={lists?.focusAreas ?? []}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
